/**
 * GA 컨텍스트 판정에 쓰는 경로 패턴 단일 소스(SSOT).
 *
 * `gaContext.ts`(런타임 헬퍼)와 `app/[locale]/layout.tsx`(inline GA 초기화 스크립트)가
 * 이 모듈의 세그먼트 배열·패턴 빌더를 공유한다. layout은 inline 스크립트 문자열에 정규식
 * source를 주입하므로(브라우저에서 `new RegExp(...)`로 재구성), 세그먼트를 한 곳만 고쳐도
 * 두 소비처가 함께 갱신된다.
 *
 * 이 모듈은 window/document에 접근하지 않아 서버(layout)·클라이언트(gaContext) 양쪽에서 안전하다.
 *
 * 주의: 매칭은 의도적으로 **비앵커(부분 문자열)**다. pathname 어느 깊이든 해당 세그먼트가 있으면
 * 매칭하므로(예: `/en/qa/...` 로케일 프리픽스를 그대로 흡수), 향후 같은 이름의 하위 세그먼트를
 * 가진 SaaS 라우트(가령 `/dashboard/private/...`, 끝의 `/dashboard/renderer`)가 생기면 의도치
 * 않게 제외될 수 있다. 새 라우트를 추가할 때 세그먼트 이름 충돌을 확인한다.
 */

/** GA 집계에서 완전히 제외(layout에서 `gtag('config')` 미호출)하는 `/세그먼트/` 경로. */
export const EXCLUDED_PATH_SEGMENTS = ['qa', 'private'] as const;

/** 제외 경로 중 뒤 슬래시 없이 끝날 수 있는 경로(쿼리 `?view=`만 붙는 `/renderer`). */
export const EXCLUDED_TAIL_PATH_SEGMENTS = ['renderer'] as const;

/** iframe 임베드(자동 page_view 차단 + 첫 메시지 시 수동 발사) 경로. */
export const EMBEDDED_PATH_SEGMENTS = ['winging'] as const;

/**
 * gaContext에서만 `excluded`로 보고 layout에는 두지 않는 경로(의도된 비대칭).
 * preview는 layout에서 자동 page_view(케이스 1)로 집계하되 trackEngagement만 noop 처리한다.
 */
export const PREVIEW_PATH_SEGMENTS = ['preview'] as const;

/** `\/(a|b)\/` 형태의 정규식 source(앞뒤 슬래시로 감싼 세그먼트 그룹). */
export const slashSegmentPattern = (segments: readonly string[]): string =>
  `\\/(${segments.join('|')})\\/`;

/** `\/(a|b)(\/|$)` 형태의 정규식 source(뒤 슬래시 또는 경로 끝 허용). */
export const tailSegmentPattern = (segments: readonly string[]): string =>
  `\\/(${segments.join('|')})(\\/|$)`;

const configSkipSlashRe = new RegExp(
  slashSegmentPattern(EXCLUDED_PATH_SEGMENTS)
);
const configSkipTailRe = new RegExp(
  tailSegmentPattern(EXCLUDED_TAIL_PATH_SEGMENTS)
);
const previewRe = new RegExp(slashSegmentPattern(PREVIEW_PATH_SEGMENTS));
const embeddedRe = new RegExp(slashSegmentPattern(EMBEDDED_PATH_SEGMENTS));

/** layout에서 `gtag('config')` 호출 자체를 건너뛰는 경로인지 (qa/private/renderer). */
export const isConfigSkipPath = (path: string): boolean =>
  configSkipSlashRe.test(path) || configSkipTailRe.test(path);

/** gaContext 전용 제외 경로인지 (preview). */
export const isPreviewPath = (path: string): boolean => previewRe.test(path);

/** iframe 임베드 경로인지 (winging). */
export const isEmbeddedPath = (path: string): boolean => embeddedRe.test(path);

/** layout inline 스크립트가 내리는 GA 초기화 결정. */
export type GaInitDecision = {
  /** `gtag('config')` 호출 자체를 건너뜀 (qa/private/renderer) */
  skipConfig: boolean;
  /** 임베드 경로라 자동 page_view를 끔 (winging) */
  isEmbedded: boolean;
};

/**
 * layout.tsx inline 스크립트가 수행하는 분기의 정본(canonical) 구현.
 * inline 스크립트는 이 함수의 브라우저 전사본이며, `gaContext.unit.spec.ts`의 동기화
 * 테스트가 이 함수와 `getAppContext`의 일관성을 검증한다.
 */
export const resolveGaInitDecision = (path: string): GaInitDecision => ({
  skipConfig: isConfigSkipPath(path),
  isEmbedded: isEmbeddedPath(path),
});

/**
 * layout.tsx의 GA 초기화 inline 스크립트(브라우저에서 실행될 문자열)를 생성한다.
 *
 * 제어 흐름(skipConfig OR 판정 → config 분기)을 layout JSX에 손으로 옮겨 적던 것을 이 순수
 * 함수로 끌어와, `gaContext.unit.spec.ts`가 생성된 스크립트를 실제 실행해 동작까지 검증한다.
 * 정규식 source는 SSOT 패턴 빌더에서 가져와 `JSON.stringify`로 안전하게 주입한다.
 */
export const buildGaInitScript = (gaId: string): string => `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());

var path = window.location.pathname;
// /qa/·/private/·/renderer 는 config 자체를 건너뜀(완전 제외), /winging/ 은 자동 page_view만 끔.
var skipConfig =
  new RegExp(${JSON.stringify(slashSegmentPattern(EXCLUDED_PATH_SEGMENTS))}).test(path) ||
  new RegExp(${JSON.stringify(tailSegmentPattern(EXCLUDED_TAIL_PATH_SEGMENTS))}).test(path);
if (!skipConfig) {
  var isEmbedded = new RegExp(${JSON.stringify(slashSegmentPattern(EMBEDDED_PATH_SEGMENTS))}).test(path);
  gtag('config', ${JSON.stringify(gaId)}, {
    send_page_view: !isEmbedded,
    app_context: isEmbedded ? 'chatbot-embedded' : 'saas',
  });
}
`;
